import {
    newServiceItemForm,
    serviceItemCostOrNull,
    serviceItemDecimalHoursOrNull,
    ServiceItemForm,
} from "@model/serviceRequests/ServiceItemForm"
import { Urgency } from "@model/Urgency"
import { WorkRequest } from "@model/workRequests/WorkRequest"

// Pure ServiceItemForm <-> WorkRequest mappings. Kept free of browser-touching imports
// (ApiService -> BuildInfo reads location.origin at module scope) so they stay unit-testable
// in node. Re-exported by MainWorkRequestService for existing consumers.

export function workRequestToServiceItemForm(wr: WorkRequest, uiElementId: number = -1): ServiceItemForm {
    const form = newServiceItemForm(uiElementId)
    return {
        ...form,
        id: wr.id,
        name: wr.workToBePerformed ?? "",
        code: wr.serviceCodeId ? wr.serviceCodeId.toString() : "",
        serviceCode: wr.serviceCodeId
            ? {
                  id: wr.serviceCodeId,
                  code: "",
                  companyId: 0,
                  name: "",
                  associatedWithWorkOrder: false,
                  associatedWithWorkRequest: false,
              }
            : null,
        laborCost: wr.actualLaborCost != null ? wr.actualLaborCost.toString() : null,
        partsCost: wr.actualPartsCost != null ? wr.actualPartsCost.toString() : null,
        otherCost: wr.actualOtherCost != null ? wr.actualOtherCost.toString() : null,
        laborHours: wr.actualLaborHours != null ? Math.floor(wr.actualLaborHours) : null,
        laborMinutes: wr.actualLaborHours != null ? Math.round((wr.actualLaborHours % 1) * 60) : null,
        notes: wr.specialInstructions || wr.notes || "",
        attachments: wr.attachments ?? [],
        filesId: wr.filesId ?? null,
        assetId: wr.assetId,
        status: wr.status,
        isSaved: false,
        workRequestType: wr.workRequestType,
        dueDate: wr.dueDate,
        dueOdometer: wr.dueOdometer,
        dueHourMeter: wr.dueHourMeter,
        hoursUntil: wr.hoursUntil,
        milesUntil: wr.milesUntil,
        daysUntil: wr.daysUntil,
        step: wr.step,
        schedule: wr.schedule,
        tasks: wr.tasks,
        notifyContacts: wr.notifyContacts ?? [],
        workPerformedBy: wr.workPerformedBy ?? [],
        workOrderId: wr.workOrderId,
        serviceSubTypeId: wr.serviceSubTypeId,
        serviceType: wr.serviceType ?? null,
        serviceScheduleStepId: wr.step?.id ?? null,
        initialTasks: wr.tasks ?? null,
        initialStep: wr.step ?? null,
        followUpWorkRequestId: wr.followUpWorkRequestId ?? null,
    }
}

export function serviceItemFormToWorkRequest(form: ServiceItemForm): WorkRequest {
    return {
        id: form.id ?? 0, // Assuming 0 for new work requests
        assetId: form.assetId ?? 0,
        step: form.step,
        schedule: form.schedule,
        billingCodeId: null, // Not present in ServiceItemForm
        workRequestType: form.workRequestType,
        estimatedLaborHours: 0,
        estimatedLaborCost: 0,
        estimatedPartsCost: 0,
        // Action Item 10811: these fields have no default - an untouched field stays null so it is
        // never persisted (or displayed) as 0.
        actualLaborHours: serviceItemDecimalHoursOrNull(form.laborHours, form.laborMinutes),
        actualLaborCost: serviceItemCostOrNull(form.laborCost),
        actualPartsCost: serviceItemCostOrNull(form.partsCost),
        actualOtherCost: serviceItemCostOrNull(form.otherCost),
        dateCreated: new Date(), // Not present in ServiceItemForm, using current date
        urgency: Urgency.Low, // Not present in ServiceItemForm, need default
        dueDate: form.dueDate,
        dueOdometer: form.dueOdometer,
        dueHourMeter: form.dueHourMeter,
        hoursUntil: form.hoursUntil,
        milesUntil: form.milesUntil,
        daysUntil: form.daysUntil,
        isUpcoming: false, // Not present in ServiceItemForm
        isOverdue: false, // Not present in ServiceItemForm
        tag: null, // Not present in ServiceItemForm
        attachments: form.attachments,
        history: [], // Not present in ServiceItemForm
        specialInstructions: form.notes, // Using notes as special instructions
        notes: form.notes,
        tasks: form.tasks,
        status: form.status,
        workOrderId: form.workOrderId,
        workToBePerformed: form.name,
        workRequestStatusFlags: [], // Not present in ServiceItemForm
        serviceCodeId: form.serviceCode?.id ?? null,
        serviceType: form.serviceType,
        serviceSubTypeId: form.serviceSubTypeId,
        notifyContacts: form.notifyContacts,
        workPerformedBy: form.workPerformedBy,
        createdBy: null, // Not present in ServiceItemForm
        createdByName: "", // Not present in ServiceItemForm
        statusFlagReason: "", // Not present in ServiceItemForm
        filesId: form.filesId ?? "",
        followUpWorkRequestId: form.followUpWorkRequestId ?? null,
        dismissed: form.dismissedReason ? true : null,
        isUnplanned: false, // Not present in ServiceItemForm
    }
}
